import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, RefreshCw, X } from 'lucide-react';
import BlueXLoader, { PageDataLoader } from '../../../components/BlueXLoader';
import '../ghost-cve.css';
import '../../early-disclosure/early-disclosure.css';
import '../../assets/assets.css';
import '../../vulnerabilities/vulnerabilities.css';
import { getEarlyIntelExposures, markEarlyIntelExposureNotified } from '../../../services/early-intel.service';
import {
  compareDates,
  compareLocaleStrings,
  getTableSortIcon,
  stableSortTableRows,
  toggleTableSortConfig,
} from '../../../utils/tableSort';
import { useTableColumnResize } from '../../../utils/tableColumnResize';
import {
  GHOST_EXPOSURE_TABLE_COLUMNS,
  GHOST_EXPOSURE_TABLE_COLUMN_WIDTHS_KEY,
} from '../../../utils/resizableTableColumns';
import { ResizableColgroup, ResizableTableHead } from '../../../components/ResizableTableHead';
import TablePagination, { TABLE_PAGE_SIZE, usePaginatedRows } from '../../../shared/components/TablePagination';

export function normalizeGhostConfidence01(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n <= 1) return Math.min(1, n);
  return Math.min(1, n / 100);
}

function coerceInternetFacing(item) {
  const raw = item?.is_internet_facing;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw !== 0;
  if (typeof raw === 'string') {
    const v = raw.trim().toLowerCase();
    if (v === 'true' || v === '1' || v === 'yes') return true;
    if (v === 'false' || v === '0' || v === 'no') return false;
  }
  const level = String(item?.exposure_level || '').trim().toLowerCase();
  if (level === 'internet') return true;
  if (level === 'internal' || level === 'isolated') return false;
  return false;
}

const EXPOSURE_LEVEL_LABELS = {
  internet: 'Internet',
  internal: 'Internal',
  isolated: 'Isolated',
  unknown: 'Unknown',
};

function formatExposureLevelLabel(raw) {
  const key = String(raw || '').trim().toLowerCase();
  if (!key) return 'Unknown';
  if (EXPOSURE_LEVEL_LABELS[key]) return EXPOSURE_LEVEL_LABELS[key];
  return key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function mapExposureAssetLabel(item) {
  const site = String(item?.site_name || '').trim();
  const hostname = String(item?.asset_hostname || '').trim();
  const ip = String(item?.asset_ip || '').trim();
  const assetId = String(item?.asset_id || '').trim();
  // Avoid duplicating Site when API historically fell back hostname → site_name.
  if (hostname && hostname !== site) return hostname;
  if (ip && ip !== site) return ip;
  if (assetId && assetId !== site) return assetId;
  return hostname || ip || assetId || '';
}

/**
 * Product-match confidence tiers (confidence_score 0‒1). Higher score = stronger belief you are affected,
 * so UI uses inverted hazard colors (red = critical). Ranges match product spec:
 * 0.85‒1.0 critical, 0.6‒0.85 high, 0.3‒0.6 medium, 0‒0.3 needs review.
 */
export function ghostConfidenceTier(score01) {
  const s = Math.min(1, Math.max(0, score01));
  if (s >= 0.85) return 'critical';
  if (s >= 0.6) return 'high';
  if (s >= 0.3) return 'medium';
  return 'review';
}

export const GHOST_CONFIDENCE_TIER_META = {
  critical: { meaning: 'Definitely affected', severity: 'Critical', priority: 'Highest' },
  high: { meaning: 'Likely affected', severity: 'High', priority: 'High' },
  medium: { meaning: 'Possible match', severity: 'Medium', priority: 'Medium' },
  review: { meaning: 'Low confidence', severity: 'Needs review', priority: 'Low' }
};

export function ghostConfidencePercentDisplay(score01) {
  return Math.round(Math.min(1, Math.max(0, score01)) * 100);
}

const formatEarlyIntelDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toISOString().slice(0, 19).replace('T', ' ');
};

const GhostCveExposuresSystemPage = ({ onBackToDashboard, exposuresFilterSeed, orgScopeQuery = null } = {}) => {
  const [exposureRows, setExposureRows] = useState([]);
  const [exposureLoading, setExposureLoading] = useState(true);
  const [exposureError, setExposureError] = useState('');
  const [exposureRevision, setExposureRevision] = useState(0);
  const [markingExposureKey, setMarkingExposureKey] = useState('');
  const [markActionMessage, setMarkActionMessage] = useState('');
  const [markNotifiedConfirm, setMarkNotifiedConfirm] = useState(null);
  const markNotifiedConfirmDescId = 'ghost-exposure-mark-notified-confirm-desc';
  const [filterDraft, setFilterDraft] = useState({
    cveQuery: '',
    internetFacingOnly: false,
    unnotifiedOnly: false
  });

  useEffect(() => {
    let mounted = true;
    const query = {
      ...(orgScopeQuery && typeof orgScopeQuery === 'object' ? orgScopeQuery : {}),
    };
    setExposureLoading(true);
    setExposureError('');
    getEarlyIntelExposures(null, query)
      .then((rows) => {
        if (!mounted) return;
        const mapped = (Array.isArray(rows) ? rows : []).map((item) => {
          const registryStatus = String(item.cve_registry_status || '').toUpperCase();
          return {
            exposureUuid: item.uu_id || item.exposure_uuid || item.uuid || item.id || null,
            cveId: item.cve_id || '',
            statusKind: registryStatus === 'NOT_FOUND' ? 'notfound' : 'product',
            statusLabel: registryStatus === 'NOT_FOUND' ? 'Not Found' : registryStatus || 'UNKNOWN',
            asset: mapExposureAssetLabel(item),
            site: item.site_name || '',
            exposureLevel: formatExposureLevelLabel(item.exposure_level || 'unknown'),
            internetFacing: coerceInternetFacing(item),
            confidence_score: item.match_confidence ?? null,
            detected: formatEarlyIntelDateTime(item.created_at),
            notified: Boolean(item.notified),
          };
        });
        setExposureRows(mapped);
      })
      .catch((err) => {
        if (!mounted) return;
        setExposureRows([]);
        setExposureError(err?.message || 'Failed to load exposures.');
      })
      .finally(() => {
        if (mounted) setExposureLoading(false);
      });
    return () => { mounted = false; };
  }, [orgScopeQuery, exposureRevision]);

  useEffect(() => {
    if (!exposuresFilterSeed || typeof exposuresFilterSeed.nonce !== 'number') return;
    const q = String(exposuresFilterSeed.cveQuery || '').trim();
    const cleared = { cveQuery: '', internetFacingOnly: false, unnotifiedOnly: false };
    if (!q) {
      setFilterDraft(cleared);
      return;
    }
    const next = { ...cleared, cveQuery: q };
    setFilterDraft(next);
  }, [exposuresFilterSeed]);

  const cveRecordUrl = (cveId) => {
    const id = encodeURIComponent(String(cveId).trim());
    return `https://www.cve.org/CVERecord?id=${id}`;
  };

  const filteredExposureRows = useMemo(() => exposureRows.filter((row) => (
    row.cveId.toLowerCase().includes(filterDraft.cveQuery.trim().toLowerCase())
    && (!filterDraft.internetFacingOnly || row.internetFacing)
    && (!filterDraft.unnotifiedOnly || !row.notified)
  )), [exposureRows, filterDraft]);

  const [exposureSortConfig, setExposureSortConfig] = useState({ key: 'detected', direction: 'desc' });

  const handleExposureSort = useCallback((key) => {
    setExposureSortConfig((prev) => toggleTableSortConfig(prev, key));
  }, []);

  const getExposureSortIcon = useCallback(
    (key) => getTableSortIcon(exposureSortConfig, key),
    [exposureSortConfig]
  );

  const sortedExposureRows = useMemo(
    () =>
      stableSortTableRows(filteredExposureRows, exposureSortConfig, (a, b, key) => {
        if (key === 'cveId') return compareLocaleStrings(a.cveId, b.cveId);
        if (key === 'status') return compareLocaleStrings(a.statusLabel, b.statusLabel);
        if (key === 'asset') return compareLocaleStrings(a.asset, b.asset);
        if (key === 'site') return compareLocaleStrings(a.site, b.site);
        if (key === 'exposureLevel') return compareLocaleStrings(a.exposureLevel, b.exposureLevel);
        if (key === 'internetFacing') return Number(a.internetFacing) - Number(b.internetFacing);
        if (key === 'confidence') {
          return (
            normalizeGhostConfidence01(a.confidence_score) - normalizeGhostConfidence01(b.confidence_score)
          );
        }
        if (key === 'detected') return compareDates(a.detected, b.detected);
        if (key === 'notified') return Number(a.notified) - Number(b.notified);
        return 0;
      }),
    [filteredExposureRows, exposureSortConfig]
  );
  const exposurePagination = usePaginatedRows(sortedExposureRows, TABLE_PAGE_SIZE);
  const visibleExposureRows = exposurePagination.pageRows;
  const { setPage: setExposurePage } = exposurePagination;
  useEffect(() => { setExposurePage(1); }, [filterDraft, exposureSortConfig, orgScopeQuery, setExposurePage]);

  const {
    widths: ghostExposureColumnWidths,
    onResizeStart: onGhostExposureColumnResizeStart,
    tableMinWidth: ghostExposureTableMinWidth,
    resizingKey: ghostExposureColumnResizingKey,
  } = useTableColumnResize(GHOST_EXPOSURE_TABLE_COLUMNS, GHOST_EXPOSURE_TABLE_COLUMN_WIDTHS_KEY);

  const tableHeaderRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [headerGutterPx, setHeaderGutterPx] = useState(8);

  const handleTableScroll = useCallback((event) => {
    if (tableHeaderRef.current) {
      tableHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  }, []);

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const CONTENT_INSET_PX = 16;

    const syncGutter = () => {
      const scrollbarLane = Math.max(0, Math.round(el.offsetWidth - el.clientWidth));
      const next = scrollbarLane + CONTENT_INSET_PX;
      setHeaderGutterPx((prev) => (prev === next ? prev : next));
    };

    syncGutter();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncGutter) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncGutter);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncGutter);
    };
  }, [exposureLoading, sortedExposureRows.length]);

  const ghostExposureTableClassName =
    'assets-table ghost-cve-table ghost-exposure-sys-table ghost-exposure-sys-table--resizable risks-page-table';
  const ghostExposureTableStyle = { width: '100%', minWidth: ghostExposureTableMinWidth };

  const exposureMetrics = useMemo(() => {
    const total = exposureRows.length;
    const internet = exposureRows.filter((r) => r.internetFacing).length;
    const unnotified = exposureRows.filter((r) => !r.notified).length;
    return { total, internet, unnotified };
  }, [exposureRows]);

  const closeMarkNotifiedConfirm = useCallback(() => {
    if (markingExposureKey) return;
    setMarkNotifiedConfirm(null);
  }, [markingExposureKey]);

  const requestMarkNotified = useCallback((row) => {
    const exposureUuid = String(row?.exposureUuid || '').trim();
    if (!exposureUuid) {
      setMarkActionMessage('Exposure UUID is missing for this row; unable to mark as notified.');
      return;
    }
    setMarkActionMessage('');
    setMarkNotifiedConfirm(row);
  }, []);

  const confirmMarkNotified = useCallback(async () => {
    const row = markNotifiedConfirm;
    const exposureUuid = String(row?.exposureUuid || '').trim();
    if (!row || !exposureUuid || markingExposureKey) return;
    setMarkActionMessage('');
    setMarkingExposureKey(exposureUuid);
    try {
      await markEarlyIntelExposureNotified(exposureUuid);
      setExposureRows((prev) => prev.map((item) => (
        String(item?.exposureUuid || '') === exposureUuid ? { ...item, notified: true } : item
      )));
      setMarkActionMessage(`Marked ${row.cveId || 'exposure'} as notified.`);
      setMarkNotifiedConfirm(null);
    } catch (err) {
      setMarkActionMessage(err?.message || 'Failed to mark exposure as notified.');
    } finally {
      setMarkingExposureKey('');
    }
  }, [markNotifiedConfirm, markingExposureKey]);

  if (exposureLoading && exposureRows.length === 0) {
    return (
      <section className="ghost-exposure-sys early-disclosures-page" aria-label="Ghost CVE Exposures">
        <PageDataLoader label="Loading exposures" />
      </section>
    );
  }

  return (
    <section className="ghost-exposure-sys early-disclosures-page" aria-label="Ghost CVE Exposures">
      <div className="ghost-exposure-back-inline-wrap">
        <button
          type="button"
          className="ghost-exposure-back-inline-btn ghost-exposure-back-inline-btn--link"
          onClick={() => {
            if (typeof onBackToDashboard === 'function') onBackToDashboard();
          }}
        >
          <span className="ghost-exposure-back-inline-icon" aria-hidden="true">
            <ArrowLeft size={18} />
          </span>
          <span>Back to intelligence feed</span>
        </button>
      </div>

      <div className="edi-stats-grid ghost-exposure-sys-metrics">
        <div className="edi-stat-card">
          <span className="edi-stat-label">Total Exposures</span>
          <span className="edi-stat-value" aria-label={`Total exposures: ${exposureMetrics.total}`}>
            {exposureMetrics.total}
          </span>
        </div>
        <div className="edi-stat-card">
          <span className="edi-stat-label">Internet-Facing</span>
          <span className="edi-stat-value" aria-label={`Internet-facing: ${exposureMetrics.internet}`}>
            {exposureMetrics.internet}
          </span>
        </div>
        <div className="edi-stat-card">
          <span className="edi-stat-label">Unnotified</span>
          <span className="edi-stat-value" aria-label={`Unnotified: ${exposureMetrics.unnotified}`}>
            {exposureMetrics.unnotified}
          </span>
        </div>
      </div>

      <div className="ghost-cve-section ghost-exposure-sys-table-outer">
        <div className="ghost-exposure-sys-table-header">
          <h2 className="assets-section-title">Exposure Details</h2>
          <div className="ghost-exposure-sys-table-toolbar">
          <div className="ghost-exposure-sys-table-toggles">
            <label className="ghost-exposure-sys-check ghost-exposure-sys-table-toggle">
              <input
                type="checkbox"
                className="ghost-exposure-sys-check-input"
                checked={filterDraft.internetFacingOnly}
                onChange={(e) =>
                  setFilterDraft((prev) => ({ ...prev, internetFacingOnly: e.target.checked }))
                }
              />
              <span>Internet-Facing Only</span>
            </label>
            <label className="ghost-exposure-sys-check ghost-exposure-sys-table-toggle">
              <input
                type="checkbox"
                className="ghost-exposure-sys-check-input"
                checked={filterDraft.unnotifiedOnly}
                onChange={(e) =>
                  setFilterDraft((prev) => ({ ...prev, unnotifiedOnly: e.target.checked }))
                }
              />
              <span>Unnotified Only</span>
            </label>
          </div>
          <div className="ghost-exposure-sys-table-search-wrap">
            <div className="ghost-exposure-sys-table-search-inner">
              <input
                type="search"
                className="ghost-exposure-sys-input ghost-exposure-sys-table-search"
                placeholder="Search by CVE ID"
                aria-label="Search exposures by CVE ID"
                value={filterDraft.cveQuery}
                onChange={(e) =>
                  setFilterDraft((prev) => ({ ...prev, cveQuery: e.target.value }))
                }
                autoComplete="off"
              />
              {filterDraft.cveQuery ? (
                <button
                  type="button"
                  className="ghost-exposure-sys-table-search-clear"
                  onClick={() =>
                    setFilterDraft((prev) => ({ ...prev, cveQuery: '' }))
                  }
                  aria-label="Clear CVE ID search"
                >
                  <X size={16} aria-hidden="true" />
                </button>
              ) : null}
            </div>
          </div>
          <button type="button" className="edi-toolbar-button" aria-label="Refresh exposures" title="Refresh exposures" disabled={exposureLoading} onClick={() => setExposureRevision((revision) => revision + 1)}><RefreshCw size={18} aria-hidden="true" /></button>
          {(filterDraft.cveQuery || filterDraft.internetFacingOnly || filterDraft.unnotifiedOnly) && (
            <button type="button" className="edi-toolbar-button" aria-label="Clear exposure filters" title="Clear exposure filters" onClick={() => setFilterDraft({ cveQuery: '', internetFacingOnly: false, unnotifiedOnly: false })}><X size={18} aria-hidden="true" /></button>
          )}
        </div>
        </div>
        {exposureError ? (
          <div className="profile-settings-message profile-settings-error" role="alert">
            {exposureError}
            <button type="button" className="edi-toolbar-button" onClick={() => setExposureRevision((revision) => revision + 1)}><RefreshCw size={16} aria-hidden="true" />Retry exposures</button>
          </div>
        ) : null}
        {markActionMessage ? (
          <div className="profile-settings-message profile-settings-info" role="status">
            {markActionMessage}
          </div>
        ) : null}
        {!exposureLoading && sortedExposureRows.length === 0 ? (
          <div className="edi-exposure-empty" role="status">
            {exposureError ? 'Exposure details could not be loaded.' : 'No exposures found for the current filter.'}
          </div>
        ) : (
        <div className="ghost-cve-panel ghost-exposure-sys-table-panel">
          <div className="assets-table-shell ghost-exposure-sys-table-shell assets-table-shell--inventory-body">
            <div
              className="risks-page-table-header-frame"
              style={{ '--risks-header-gutter': `${headerGutterPx}px` }}
            >
              <div ref={tableHeaderRef} className="risks-page-table-header">
                <table
                  className={`${ghostExposureTableClassName} risks-page-table--header`}
                  style={ghostExposureTableStyle}
                >
                  <ResizableColgroup columns={GHOST_EXPOSURE_TABLE_COLUMNS} widths={ghostExposureColumnWidths} />
                  <ResizableTableHead
                    columns={GHOST_EXPOSURE_TABLE_COLUMNS}
                    onResizeStart={onGhostExposureColumnResizeStart}
                    resizingKey={ghostExposureColumnResizingKey}
                    sortConfig={exposureSortConfig}
                    onSort={handleExposureSort}
                    getSortIcon={getExposureSortIcon}
                    resizeHandleClassName="risks-page-col-resize-handle"
                  />
                </table>
              </div>
              <div className="risks-page-table-header-gutter" aria-hidden="true" />
            </div>
            <div
              ref={tableScrollRef}
              className="assets-inventory-table-scroll assets-inventory-table-scroll--body risks-page-table-scroll ghost-exposure-sys-table-scroll"
              onScroll={handleTableScroll}
            >
            <table
              className={`${ghostExposureTableClassName} risks-page-table--body`}
              style={ghostExposureTableStyle}
            >
              <ResizableColgroup columns={GHOST_EXPOSURE_TABLE_COLUMNS} widths={ghostExposureColumnWidths} />
              <tbody>
                {exposureLoading ? (
                  <tr>
                    <td colSpan={9} className="assets-empty-cell ghost-exposure-sys-empty">
                      <BlueXLoader size="small" label="Loading exposures" />
                    </td>
                  </tr>
                ) : sortedExposureRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="assets-empty-cell ghost-exposure-sys-empty">
                      No exposures found for the current filter.
                    </td>
                  </tr>
                ) : (
                  visibleExposureRows.map((row) => {
                    const confidenceAssessed = row.confidence_score != null;
                    const conf01 = normalizeGhostConfidence01(
                      row.confidence_score != null ? row.confidence_score : row.confidencePct
                    );
                    const confTier = ghostConfidenceTier(conf01);
                    const confMeta = GHOST_CONFIDENCE_TIER_META[confTier];
                    const confPct = ghostConfidencePercentDisplay(conf01);
                    const confTitle = `${confMeta.meaning}  ${confMeta.severity}  Priority ${confMeta.priority} (${confPct}% match confidence, 01 scale).`;
                    const statusClass =
                      row.statusKind === 'product'
                        ? 'ghost-status-badge ghost-exposure-sys-status--product'
                        : 'ghost-status-badge ghost-exposure-sys-status--notfound';
                    return (
                      <tr key={`${row.cveId}-${row.asset}`}>
                        <td>
                          <a
                            className="ghost-cve-link"
                            href={cveRecordUrl(row.cveId)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {row.cveId}
                          </a>
                        </td>
                        <td>
                          <span className={statusClass}>{row.statusLabel}</span>
                        </td>
                        <td className="ghost-cve-product">{row.asset}</td>
                        <td className="ghost-exposure-sys-site-cell">
                          <span>{row.site || ''}</span>
                        </td>
                        <td>
                          <span className="ghost-exposure-sys-level">{row.exposureLevel}</span>
                        </td>
                        <td className="assets-cell-center">
                          <span
                            className={
                              row.internetFacing
                                ? 'ghost-exposure-sys-bool ghost-exposure-sys-bool--yes'
                                : 'ghost-exposure-sys-bool ghost-exposure-sys-bool--no'
                            }
                          >
                            {row.internetFacing ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="assets-cell-center">
                          <div className="edi-confidence-cell ghost-exposure-sys-confidence" title={confidenceAssessed ? confTitle : 'Match confidence has not been assessed.'}>
                            <span
                              className={confidenceAssessed ? `edi-confidence-pct edi-confidence-pct--${confTier}` : 'ghost-confidence-unassessed'}
                              aria-label={confidenceAssessed ? `${confPct}% match confidence. ${confMeta.meaning}, ${confMeta.severity}, priority ${confMeta.priority}` : 'Match confidence not assessed'}
                            >
                              {confidenceAssessed ? `${confPct}%` : 'Not assessed'}
                            </span>
                          </div>
                        </td>
                        <td className="ghost-cve-date">{row.detected}</td>
                        <td className="assets-cell-center">
                          {row.notified ? (
                            <span
                              className="ghost-exposure-sys-notify-icon ghost-exposure-sys-notify-icon--done"
                              title="Notified"
                              aria-label="Notified"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path
                                  d="M20 6L9 17l-5-5"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="ghost-exposure-sys-notify-btn"
                              onClick={() => requestMarkNotified(row)}
                              disabled={markingExposureKey === String(row?.exposureUuid || '')}
                              title="Mark notified"
                              aria-label="Mark notified"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path
                                  d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M13.73 21a2 2 0 01-3.46 0"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            </div>
          </div>
          <TablePagination {...exposurePagination} itemLabel="exposures" />
        </div>
        )}
      </div>

      {markNotifiedConfirm ? (
        <div
          className="assets-detail-modal-overlay assets-exclude-confirm-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeMarkNotifiedConfirm();
          }}
        >
          <div
            className="assets-exclude-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-label="Confirm mark notified"
            aria-describedby={markNotifiedConfirmDescId}
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              onClick={closeMarkNotifiedConfirm}
              aria-label="Close"
              disabled={Boolean(markingExposureKey)}
            >
              ✕
            </button>
            <p id={markNotifiedConfirmDescId} className="assets-exclude-confirm-desc">
              Are you sure you want to mark this exposure as notified?
              <span className="assets-exclude-confirm-asset">
                {markNotifiedConfirm.cveId || 'Exposure'}
                {markNotifiedConfirm.asset ? ` · ${markNotifiedConfirm.asset}` : ''}
              </span>
            </p>
            <div className="assets-exclude-confirm-actions">
              <button
                type="button"
                className="assets-exclude-confirm-btn"
                onClick={confirmMarkNotified}
                disabled={Boolean(markingExposureKey)}
              >
                Mark notified
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default GhostCveExposuresSystemPage;
